import axios from 'axios';
import toast from 'react-hot-toast';

// ─────────────────────────────────────────────────────────────────────────────
// URL BASE DE LA API
//
// En LOCAL:      Lee VITE_API_BASE_URL desde el archivo .env.local
//                → apunta a http://localhost:5000/api
//
// En PRODUCCIÓN: La variable no existe, usa la URL de EvenNode hardcodeada.
//
// De esta forma no hay que tocar nada para cambiar entre entornos,
// solo tener o no tener el archivo .env.local.
// ─────────────────────────────────────────────────────────────────────────────
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://temis-oficial.eu-4.evennode.com/api';

// Crear instancia de axios
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 900000,
  headers: {
    'Content-Type': 'application/json',
  }
});

// Debug: mostrar la URL que se está usando
console.log('📡 API configurado para:', API_BASE_URL);
console.log('🏠 Modo:', import.meta.env.DEV ? 'LOCAL (desarrollo)' : 'PRODUCCIÓN');

// Interceptor para requests
api.interceptors.request.use(
  (config) => {
    // Agregar token si existe
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Log de request en desarrollo
    if (import.meta.env.DEV) {
      console.log(`📤 API Request: ${config.method?.toUpperCase()} ${config.url}`, {
        params: config.params,
        data: config.data
      });
    }

    return config;
  },
  (error) => {
    console.error('❌ Request Error:', error);
    return Promise.reject(error);
  }
);

// Interceptor para responses
api.interceptors.response.use(
  (response) => {
    // Log de response exitoso en desarrollo
    if (import.meta.env.DEV) {
      console.log(`✅ API Response: ${response.config.method?.toUpperCase()} ${response.config.url}`, {
        status: response.status,
        data: response.data
      });
    }

    return response;
  },
  (error) => {
    // Log de error
    console.error('❌ API Error:', {
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status,
      message: error.response?.data?.message || error.message
    });

    // Manejar errores específicos
    if (error.response) {
      const { status, data } = error.response;

      switch (status) {
        case 401:
          // Token expirado o inválido
          if (data.message?.includes('Token') || data.message?.includes('token')) {
            localStorage.removeItem('token');
            delete api.defaults.headers.common['Authorization'];

            // Solo mostrar toast si no estamos en la página de login
            if (!window.location.pathname.includes('/login')) {
              toast.error('Sesión expirada. Por favor, inicia sesión nuevamente.');
              setTimeout(() => {
                window.location.href = '/login';
              }, 1500);
            }
          }
          break;

        case 403:
          toast.error('No tienes permisos para realizar esta acción');
          break;

        case 404:
          // No mostrar toast para 404, lo maneja cada componente
          break;

        case 429:
          toast.error('Demasiadas peticiones. Intenta de nuevo más tarde.');
          break;

        case 500:
          toast.error('Error interno del servidor. Intenta de nuevo más tarde.');
          break;

        default:
          // Otros errores
          if (data.message) {
            toast.error(data.message);
          }
          break;
      }
    } else if (error.request) {
      // Error de red
      toast.error('Error de conexión. Verifica tu conexión a internet.');
    } else {
      // Error de configuración
      toast.error('Error interno de la aplicación');
    }

    return Promise.reject(error);
  }
);

// Wrapper para métodos HTTP
export const httpMethods = {
  get: async (url, config = {}) => {
    try {
      const response = await api.get(url, config);
      return response;
    } catch (error) {
      throw error;
    }
  },

  post: async (url, data = {}, config = {}) => {
    try {
      const response = await api.post(url, data, config);
      return response;
    } catch (error) {
      throw error;
    }
  },

  put: async (url, data = {}, config = {}) => {
    try {
      const response = await api.put(url, data, config);
      return response;
    } catch (error) {
      throw error;
    }
  },

  patch: async (url, data = {}, config = {}) => {
    try {
      const response = await api.patch(url, data, config);
      return response;
    } catch (error) {
      throw error;
    }
  },

  delete: async (url, config = {}) => {
    try {
      const response = await api.delete(url, config);
      return response;
    } catch (error) {
      throw error;
    }
  }
};

// Funciones específicas para cada módulo
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  logout: () => api.post('/auth/logout'),
  verifyToken: () => api.post('/auth/verify'),
  getProfile: () => api.get('/auth/profile'),
  changePassword: (passwordData) => api.put('/auth/change-password', passwordData),
};

export const traceabilityAPI = {
  getSpanishLeagues: () => api.get('/traceability/leagues/spanish'),
  getTeamsByLeague: (leagueId, year = '2023') =>
    api.get(`/traceability/leagues/${leagueId}/teams`, { params: { year } }),
  getTeamSquad: (teamId, year = '2023') =>
    api.get(`/traceability/teams/${teamId}/squad`, { params: { year } }),
  compareTeams: (team1Id, team2Id) =>
    api.post('/traceability/compare', { team1Id, team2Id }),
  searchTeams: (query) =>
    api.get('/traceability/search/teams', { params: { query } }),
  getPlayerHistory: (playerId) =>
    api.get(`/traceability/players/${playerId}/history`),
  getStats: () => api.get('/traceability/stats'),
};

export const reportsAPI = {
  getReports: (params = {}) => api.get('/reports', { params }),
  getReportById: (id) => api.get(`/reports/${id}`),
  createReport: (reportData) => api.post('/reports', reportData),
  updateReport: (id, reportData) => api.put(`/reports/${id}`, reportData),
  deleteReport: (id) => api.delete(`/reports/${id}`),
  searchReports: (query, limit = 20) =>
    api.get('/reports/search', { params: { query, limit } }),
  getStats: () => api.get('/reports/stats'),
  exportReports: (format = 'json', filters = {}) =>
    api.get('/reports/export', { params: { format, ...filters } }),
  syncFromMake: (makeData) => api.post('/reports/sync/make', makeData),
};

// Funciones de utilidad
export const apiUtils = {
  // Configurar token globalmente
  setAuthToken: (token) => {
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      localStorage.setItem('token', token);
    } else {
      delete api.defaults.headers.common['Authorization'];
      localStorage.removeItem('token');
    }
  },

  // Obtener token actual
  getAuthToken: () => {
    return localStorage.getItem('token');
  },

  // Verificar si hay token
  hasAuthToken: () => {
    return !!localStorage.getItem('token');
  },

  // Limpiar autenticación
  clearAuth: () => {
    delete api.defaults.headers.common['Authorization'];
    localStorage.removeItem('token');
  },

  // Health check
  healthCheck: () => api.get('/health'),

  // Información de la API
  getApiInfo: () => api.get('/'),
};

export const injuriesAPI = {
  // Función para analizar UN equipo
  analyzeTeam: (teamId, year = '2025') =>
    api.post('/injuries/analyze-team', { teamId, year }),
  // Analizar lesiones de 2 equipos
  analyzeTeams: (team1Id, team2Id, year = '2025') =>
    api.post('/injuries/analyze-teams', { team1Id, team2Id, year }),

  // Obtener equipos agrupados por competición
  getTeamsByCompetition: () => api.get('/injuries/teams-by-competition'),

  // Verificar estado del servicio de lesiones
  getStatus: () => api.get('/injuries/status'),

  // Obtener documentación del módulo
  getDocumentation: () => api.get('/injuries')
};

export const hotLeaguesAPI = {
  // Buscar en UN equipo
  searchByTeam: (teamId, year = '2026') =>
    api.post('/hot-leagues/players', { teamId, year }),

  // Buscar en TODA una competición
  searchByCompetition: (competitionId, year = '2026') =>
    api.post('/hot-leagues/competition', { competitionId, year }),

  // Obtener equipos disponibles
  getAvailableTeams: () => api.get('/hot-leagues/teams'),

  // Verificar estado
  checkStatus: () => api.get('/hot-leagues/status'),
};

// Configurar token al cargar si existe
const existingToken = localStorage.getItem('token');
if (existingToken) {
  api.defaults.headers.common['Authorization'] = `Bearer ${existingToken}`;
}

export default api;